import {
  ROUGH_NOTATION_BRACKETS,
  ROUGH_NOTATION_TYPES,
  type RoughNotationBracket,
} from '../../../highlights/roughNotationTypes';
import type { RoughNotation, StandoutMarker, StandoutNotation } from './types';

/**
 * Shared marker palette. A standout's list-row icon and the mark drawn over it
 * in the document must match, so both sides read color + notation from here.
 */

export const STANDOUT_MARKERS: readonly StandoutMarker[] = [
  {
    notation: 'highlight',
    color: '#F4B400',
    strokeWidth: 0,
    padding: 2,
    iterations: 0,
    multiline: true,
  },
  {
    notation: 'circle',
    color: '#EF6C5C',
    strokeWidth: 2,
    padding: 10,
    iterations: 2,
    multiline: false,
  },
  {
    notation: 'box',
    color: '#14B8A6',
    strokeWidth: 2,
    padding: 8,
    iterations: 2,
    multiline: false,
  },
  {
    notation: 'underline',
    color: '#8B5CF6',
    strokeWidth: 2,
    padding: 3,
    iterations: 2,
    multiline: true,
  },
  {
    notation: 'bracket',
    color: '#3B82F6',
    strokeWidth: 2,
    padding: 6,
    iterations: 1,
    multiline: false,
    brackets: ['left'],
  },
  {
    notation: 'strike-through',
    color: '#22C55E',
    strokeWidth: 2,
    padding: 3,
    iterations: 2,
    multiline: true,
  },
];

/**
 * The marker for a standout, derived from its id. Deterministic on purpose: a
 * random draw would re-roll on every refetch and leave a row's icon mismatched
 * with the mark already drawn on the document.
 */
export const standoutMarkerFor = (id: string): StandoutMarker => {
  // FNV-1a — spreads sequential ids across the palette instead of cycling them.
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.codePointAt(i) as number;
    hash = Math.imul(hash, 0x01000193);
  }
  const index = (hash >>> 0) % STANDOUT_MARKERS.length;
  return STANDOUT_MARKERS[index] as StandoutMarker;
};

/** The types a standout can be given when it is added or changed. */
export const EDITABLE_STANDOUT_NOTATIONS: readonly StandoutNotation[] = [
  'highlight',
  'circle',
  'box',
  'underline',
  'bracket',
  'strike-through',
];

const isStandoutNotation = (type: string): type is StandoutNotation =>
  (ROUGH_NOTATION_TYPES as readonly string[]).includes(type);

const isBracket = (side: string): side is RoughNotationBracket =>
  (ROUGH_NOTATION_BRACKETS as readonly string[]).includes(side);

/**
 * The marker the BE sent for a standout, or the id-derived one when it sent
 * none (assets generated before it did) or a type rough-notation can't draw.
 */
export const standoutMarkerFrom = (
  id: string,
  roughNotation?: RoughNotation | null,
): StandoutMarker => {
  if (!roughNotation || !isStandoutNotation(roughNotation.type)) {
    return standoutMarkerFor(id);
  }
  const isHighlight = roughNotation.type === 'highlight';
  const brackets = roughNotation.brackets.filter(isBracket);
  return {
    notation: roughNotation.type,
    color: roughNotation.color,
    // A highlight is a plain fill; the viewer tints it, so drop the stroke.
    strokeWidth: isHighlight ? 0 : roughNotation.stroke_width,
    padding: roughNotation.padding,
    iterations: isHighlight ? 0 : roughNotation.iterations,
    multiline: roughNotation.multiline,
    // None left → the viewer's default side, rather than drawing nothing.
    brackets: brackets.length > 0 ? brackets : undefined,
    animationDelay: roughNotation.animation_delay,
    animationDuration: roughNotation.animation_duration,
  };
};
